import type { Condition, ConditionGroup, ConditionRule } from "../types/template.js";

/**
 * Compiles a structured condition (built by the UI) to expression source.
 * Raw string conditions are returned unchanged.
 */
export function compileCondition(condition: Condition | undefined): string | null {
  if (condition === undefined || condition === null) return null;
  if (typeof condition === "string") return condition.trim() || null;
  const src = compileGroup(condition);
  return src || null;
}

function compileGroup(group: ConditionGroup): string {
  const parts = group.rules
    .map((r) => ("op" in r ? compileGroup(r) : compileRule(r)))
    .filter((s): s is string => !!s);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0]!;
  return parts.map((p) => `(${p})`).join(group.op === "and" ? " and " : " or ");
}

function literal(v: ConditionRule["right"]): string {
  if (v === null || v === undefined) return "null";
  if (typeof v === "string") {
    const m = /^\{\{\s*(.+?)\s*\}\}$/.exec(v);
    if (m) return m[1]!;
    return JSON.stringify(v);
  }
  return String(v);
}

function compileRule(rule: ConditionRule): string {
  const left = rule.left.trim();
  if (!left) return "";
  switch (rule.operator) {
    case "eq":
      return `${left} == ${literal(rule.right)}`;
    case "neq":
      return `${left} != ${literal(rule.right)}`;
    case "gt":
      return `${left} > ${literal(rule.right)}`;
    case "gte":
      return `${left} >= ${literal(rule.right)}`;
    case "lt":
      return `${left} < ${literal(rule.right)}`;
    case "lte":
      return `${left} <= ${literal(rule.right)}`;
    case "contains":
      return `contains(${left}, ${literal(rule.right)})`;
    case "startsWith":
      return `startsWith(${left}, ${literal(rule.right)})`;
    case "endsWith":
      return `endsWith(${left}, ${literal(rule.right)})`;
    case "in":
      return `${left} in ${Array.isArray(rule.right) ? JSON.stringify(rule.right) : literal(rule.right)}`;
    case "isEmpty":
      return `isEmpty(${left})`;
    case "isNotEmpty":
      return `isNotEmpty(${left})`;
    case "isTrue":
      return `${left} == true`;
    case "isFalse":
      return `not ${left}`;
  }
}

export const CONDITION_OPERATORS: Array<{ value: ConditionRule["operator"]; label: string; needsValue: boolean; types?: string[] }> = [
  { value: "eq", label: "equals", needsValue: true },
  { value: "neq", label: "does not equal", needsValue: true },
  { value: "gt", label: "is greater than", needsValue: true, types: ["number", "currency", "percent", "date", "datetime"] },
  { value: "gte", label: "is at least", needsValue: true, types: ["number", "currency", "percent", "date", "datetime"] },
  { value: "lt", label: "is less than", needsValue: true, types: ["number", "currency", "percent", "date", "datetime"] },
  { value: "lte", label: "is at most", needsValue: true, types: ["number", "currency", "percent", "date", "datetime"] },
  { value: "contains", label: "contains", needsValue: true, types: ["string", "array"] },
  { value: "startsWith", label: "starts with", needsValue: true, types: ["string"] },
  { value: "endsWith", label: "ends with", needsValue: true, types: ["string"] },
  { value: "isEmpty", label: "is empty", needsValue: false },
  { value: "isNotEmpty", label: "is not empty", needsValue: false },
  { value: "isTrue", label: "is true", needsValue: false, types: ["boolean"] },
  { value: "isFalse", label: "is false", needsValue: false, types: ["boolean"] }
];
